import { Collection } from 'firestorter';

import BaseDocument from '../../Model/BaseDocument';
import CollectionName from '../../utils/collections';

/**
 * Every challenge config whose `field` equals `value`.
 *
 * @param {string} field - The challenge config field to match.
 * @param {string} value - The value to match.
 * @returns {Promise<Array<ChallengeConfig>>} The challenge configs found.
 */
const getChallengeConfigsBy = async (field, value) => {
  const challengeConfigCollection = new Collection(CollectionName.CHALLENGE_CONFIG, {
    // eslint-disable-next-line no-use-before-define
    createDocument: (source, opts) => new ChallengeConfig(source.id, opts),
    query: (ref) => ref.where(field, '==', value),
  });
  await challengeConfigCollection.fetch();
  return challengeConfigCollection.docs;
};

// The fields a challenge names its programs in: its single program, or its home and its gym program.
const PROGRAM_FIELDS = ['programId', 'homeProgramId', 'gymProgramId'];

/**
 * ChallengeConfig model for storing challenge configuration data
 *
 * @class ChallengeConfig
 * @extends BaseDocument
 */
class ChallengeConfig extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.CHALLENGE_CONFIG}/${id}`, opts);
  }

  /**
   * Get the meal plan ID associated with this challenge
   * @returns {string} The meal plan ID
   */
  get mealPlanId() {
    return this.data.mealPlanId || '';
  }

  /**
   * Get the program ID associated with this challenge. Empty on a challenge that runs on a home and a gym
   * program instead.
   * @returns {string} The program ID
   */
  get programId() {
    return this.data.programId || '';
  }

  /**
   * Get the home program of a challenge whose clients switch between a home and a gym program.
   * @returns {string} The home program ID, empty on a single-program challenge
   */
  get homeProgramId() {
    return this.data.homeProgramId || '';
  }

  /**
   * Get the gym program of a challenge whose clients switch between a home and a gym program.
   * @returns {string} The gym program ID, empty on a single-program challenge
   */
  get gymProgramId() {
    return this.data.gymProgramId || '';
  }

  /**
   * Whether the challenge runs on a home and a gym program rather than a single one: both are set, as the server
   * (`listChallengePrograms`) reads it. With only one, clients get `programId`.
   * @returns {boolean}
   */
  get hasHomeAndGymPrograms() {
    return !!this.homeProgramId && !!this.gymProgramId;
  }

  /**
   * Get the tag ID associated with this challenge
   * @returns {string} The tag ID
   */
  get tag() {
    return this.data.tag || '';
  }

  /**
   * Creates a new challenge config document with given data.
   *
   * @param {object} data - New challenge config data containing mealPlanId and either programId or
   *   homeProgramId and gymProgramId.
   * @returns {Promise<ChallengeConfig>} New challenge config document.
   */
  static async addDoc(data) {
    const challengeConfigCollection = new Collection(CollectionName.CHALLENGE_CONFIG);
    const doc = await challengeConfigCollection.add({
      ...data,
      createdAt: new Date(),
    });
    const challengeConfig = new ChallengeConfig(doc.id);
    await challengeConfig.init();
    return challengeConfig;
  }

  /**
   * Get a challenge config by its id.
   *
   * @param {string} id - The id of the challenge config to get.
   * @returns {Promise<ChallengeConfig>} The challenge config.
   */
  static async getChallengeConfigById(id) {
    const challengeConfig = new ChallengeConfig(id);
    await challengeConfig.init();
    return challengeConfig.exists ? challengeConfig : null;
  }

  /**
   * Get the challenge configs a program belongs to, as a challenge's single program or as its home or gym
   * program. A program can be attached to several challenges.
   *
   * @param {string} programId - The id of the program.
   * @returns {Promise<Array<ChallengeConfig>>} The challenge configs using the program, each once.
   */
  static async getChallengeConfigsByProgramId(programId) {
    // This SDK can't OR across fields, so each field is queried on its own and the results merged by id.
    const configsByField = await Promise.all(
      PROGRAM_FIELDS.map((field) => getChallengeConfigsBy(field, programId)),
    );
    const configsById = new Map(configsByField.flat().map((config) => [config.id, config]));
    return [...configsById.values()];
  }

  /**
   * Get the challenge configs a meal plan belongs to. A meal plan can be attached
   * to several challenges.
   *
   * @param {string} mealPlanId - The id of the meal plan.
   * @returns {Promise<Array<ChallengeConfig>>} The challenge configs using the meal plan.
   */
  static getChallengeConfigsByMealPlanId(mealPlanId) {
    return getChallengeConfigsBy('mealPlanId', mealPlanId);
  }
}

export default ChallengeConfig;
